// What the Workspaces pages say of a parent's named blueprints and of the
// blueprint each child uses (02-DESIGN/13-blueprints/01-the-blueprint.md §
// named blueprints, § the console: Workspaces; 02-DESIGN/12-workspace-hierarchy/01-the-relation.md
// § the Workspaces page; decision 0052).

import { blueprint } from "@impire-io/sdk";

import { tenantPath } from "../../address/address.ts";
import { CODE_NO_SERVICE, isRefusal } from "../../platform/bus.ts";
import type { ChildRow, Document, Entry, LibraryDocument, Secret } from "../../wire/blueprint.ts";

/** BLUEPRINTS_TAB is the Workspaces page's second tab, the library, carried in a link's navigation state and never in the address. */
export const BLUEPRINTS_TAB = "blueprints";

/** childPath is a child's page under Workspaces, `workspaces/<workspace>`. */
export function childPath(tenant: string, child: string): string {
  return tenantPath(tenant, "workspaces", child);
}

/** blueprintPath is a named blueprint's page, `workspaces/blueprints/<name>`. */
export function blueprintPath(tenant: string, name: string): string {
  return tenantPath(tenant, "workspaces", "blueprints", name);
}

/** revisionPath is one of a blueprint's revisions, `workspaces/blueprints/<name>/history/<revision>` (hq decision 0069). */
export function revisionPath(tenant: string, name: string, revision: number): string {
  return tenantPath(tenant, "workspaces", "blueprints", name, "history", String(revision));
}

/** revisionOf reads a revision from the address: a whole number from 1, or nothing. */
export function revisionOf(text: string): number | undefined {
  return /^[1-9][0-9]{0,15}$/.test(text) ? Number(text) : undefined;
}

/** saidOf reads what a page that moved here said it did, from the navigation state, `{ said }`. */
export function saidOf(state: unknown): string | undefined {
  const said = (state as { said?: unknown } | null)?.said;
  return typeof said === "string" && said !== "" ? said : undefined;
}

/**
 * restoreGuard is the revision guard a restore sends, as *Save* does: the
 * revision saved now, or 0 where none is — a deleted blueprint is written
 * anew. Undefined while the current read is pending or refused: a restore
 * then writes nothing, never an unguarded overwrite.
 */
export function restoreGuard(
  current: { state: "pending" } | { state: "refused" } | { state: "ready"; value: Entry | undefined },
): string | undefined {
  if (current.state !== "ready") {
    return undefined;
  }
  const v = current.value;
  return String(v !== undefined && v.op === blueprint.EntryOp.put ? v.revision : 0);
}

/** reachWords says whom a restore reaches, from the read of who uses the blueprint. */
export function reachWords(
  users: { state: "pending" } | { state: "refused" } | { state: "ready"; value: number },
): string {
  if (users.state === "pending") {
    return "every workspace using it follows; which ones is still being read";
  }
  if (users.state === "refused") {
    return "every workspace using it follows; which ones could not be read";
  }
  return users.value === 0
    ? "no workspace uses it yet"
    : users.value === 1
      ? "the one workspace using it follows"
      : `the ${String(users.value)} workspaces using it follow`;
}

/**
 * blueprintFailure is what failed when a blueprint read did not answer, in
 * the person's words (01-the-console.md § conventions: a reply it cannot
 * draw): an install that runs no blueprint service says so, and anything
 * else is the refusal as the console words it.
 */
export function blueprintFailure(err: unknown): Error {
  if (isRefusal(err, CODE_NO_SERVICE)) {
    return new Error("Nothing answers for blueprints on this install yet, so they cannot be read here.");
  }
  return err instanceof Error ? err : new Error(String(err));
}

/** libraryOf reads a named blueprint's text, or nothing where it is not one. */
export function libraryOf(entry: Entry | undefined): LibraryDocument | undefined {
  if (entry?.document === undefined) {
    return undefined;
  }
  try {
    return JSON.parse(entry.document) as LibraryDocument;
  } catch {
    return undefined;
  }
}

/** settingsOf reads a settings document's text as its fields, or nothing where it is not a set of fields. */
export function settingsOf(text: string | undefined): Record<string, unknown> | undefined {
  if (text === undefined) {
    return undefined;
  }
  try {
    const v = JSON.parse(text) as unknown;
    return typeof v === "object" && v !== null && !Array.isArray(v)
      ? (v as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * usesWords is a child's Blueprint column: the named blueprint it uses,
 * *updating* while its document is behind it and *needs a secret* while a
 * slot has no value for it; *its own blueprint* for a document written
 * before named blueprints or by the install's operator; *no blueprint yet*.
 */
export function usesWords(row: ChildRow | undefined, managed: boolean): { name: string; notes: string[] } {
  if (row?.blueprint !== undefined && row.blueprint !== "") {
    const notes: string[] = [];
    if ((row.latest ?? 0) > (row.from ?? 0)) {
      notes.push("updating");
    }
    if ((row.missing ?? []).length > 0) {
      notes.push("needs a secret");
    }
    return { name: row.blueprint, notes };
  }
  if (row?.own === true || managed) {
    return { name: "its own blueprint", notes: [] };
  }
  return { name: "no blueprint yet", notes: [] };
}

/** sealedSecrets is the sealed secrets a child's resolved document carries: its own values. */
export function sealedSecrets(doc: Document | undefined): Secret[] {
  return (doc?.secrets ?? []).filter((s) => s.sealed !== "");
}

/** nameFrom suggests a blueprint name from a workspace's slug or name. */
export function nameFrom(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}
